/**
 * WebAudio synth + gesture unlock.
 *
 * Browsers (and Telegram WebView in particular) start AudioContext in
 * the `suspended` state. Calling `osc.start()` on a suspended ctx
 * silently produces no sound — the previous overlay-inline version
 * never resumed, so the chime never played in TG. Fix: install a
 * one-shot global gesture listener that resumes on the first real
 * user interaction, and additionally try `resume()` at play-time so
 * a chime triggered from inside a tap handler still fires on the
 * very first round of the session.
 *
 * Sounds are synthesised (no asset to ship). Three sine notes for a
 * win, two for a loss.
 */

import { getSoundEnabled } from './prefs';

type Ctor = typeof AudioContext;

let ctxSingleton: AudioContext | null = null;
let unlockInstalled = false;

function findCtor(): Ctor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: Ctor;
    webkitAudioContext?: Ctor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function ensureCtx(): AudioContext | null {
  if (ctxSingleton) return ctxSingleton;
  const C = findCtor();
  if (!C) return null;
  try {
    ctxSingleton = new C();
  } catch {
    return null;
  }
  return ctxSingleton;
}

/**
 * Idempotent. Mount-time hook for the component that owns the chime
 * lifecycle — installs a one-shot listener on the first user gesture
 * that creates and resumes the audio context. Subsequent gestures are
 * no-op (listener self-removes).
 */
export function setupAudioUnlock(): void {
  if (typeof window === 'undefined' || unlockInstalled) return;
  unlockInstalled = true;
  const events = ['pointerdown', 'touchstart', 'keydown'] as const;
  const handler = () => {
    const ctx = ensureCtx();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {
        /* TG WebView may still refuse — leave suspended; next gesture retries */
      });
    }
    if (ctx && ctx.state === 'running') {
      events.forEach((e) => window.removeEventListener(e, handler));
    }
  };
  events.forEach((e) =>
    window.addEventListener(e, handler, { passive: true }),
  );
}

export function playChime(win: boolean): void {
  if (!getSoundEnabled()) return;
  try {
    const ctx = ensureCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      // Best-effort: works when called from inside a user gesture.
      // The persistent unlock listener catches the next gesture
      // otherwise, so subsequent chimes will fire.
      ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;
    const notes = win ? [523.25, 659.25, 783.99] : [440, 369.99];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.32);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.34);
    });
  } catch {
    /* non-critical */
  }
}

/** Test-only — drop the singleton so each test starts with a fresh ctx. */
export function __resetAudio(): void {
  ctxSingleton = null;
  unlockInstalled = false;
}
